import { useState } from 'react'
import { useApolloClient } from '@apollo/client'
import { LicenseOverview } from '@/components/campaigns/LicenseOverviewForm'
import { GET_LICENSE_OVERVIEW } from '@/graphql/ads/prompt-suggestion-queries'

interface LicenseOverviewData {
    licenseOverview: LicenseOverview
}

/** The Add License form fields the AI helpers send for a license that is not saved yet. */
export interface LicenseDraftFields {
    name: string
    address: string
    keywords: string[]
    purpose: string
    brief: string
}

/**
 * A license that is not saved yet (Add License): its form fields and the one overview its AI helpers share, kept by
 * the form until the license is created.
 */
export interface LicenseDraft {
    fields: LicenseDraftFields
    overview: LicenseOverview | null
    onOverviewSaved: (overview: LicenseOverview) => void
}

export const OVERVIEW_REQUIRED = 'LICENSE_OVERVIEW_REQUIRED'

export const errorCodeOf = (error: unknown): string | null => {
    const graphQLErrors = (error as { graphQLErrors?: { extensions?: { code?: string } }[] })?.graphQLErrors
    return graphQLErrors?.[0]?.extensions?.code ?? null
}

/** The input field a GraphQL error names in its extensions, such as 'systemPrompt'. */
export const errorFieldOf = (error: unknown): string | null => {
    const graphQLErrors = (error as { graphQLErrors?: { extensions?: { field?: string } }[] })?.graphQLErrors
    return graphQLErrors?.[0]?.extensions?.field ?? null
}

/** The overview fields the API takes for a license that is not saved yet (LicenseOverviewDraftInput). */
export const overviewDraftInputOf = (overview: Partial<LicenseOverview> | null | undefined) => ({
    primaryUrl: overview?.primaryUrl ?? null,
    overview: overview?.overview ?? null,
    socialUrls: overview?.socialUrls ?? [],
    styleGuideUrl: overview?.styleGuideUrl ?? null,
})

/**
 * The license overview step shared by the license AI helpers, for a saved license (`campaignId`) or one that is not
 * saved yet (`draft`). `open` opens the overview form prefilled with the saved overview, or the draft's (false when
 * loading failed); `saved` keeps the overview the form returned, hands a draft's to the form that holds it, and closes
 * the form. `targetOf` gives the variables naming the license for a suggestion request. A draft's overview is shared
 * by its helpers, so it is always read from the draft rather than kept here.
 */
export const useLicenseOverviewStep = (campaignId?: string | null, draft?: LicenseDraft) => {
    const client = useApolloClient()
    const [overview, setOverview] = useState<LicenseOverview | null>(null)
    const [formOpen, setFormOpen] = useState(false)
    const [loading, setLoading] = useState(false)

    const current = campaignId ? overview : (draft?.overview ?? null)

    const open = async (): Promise<boolean> => {
        if (!campaignId) {
            setFormOpen(true)
            return true
        }
        setLoading(true)
        try {
            const { data } = await client.query<LicenseOverviewData>({
                query: GET_LICENSE_OVERVIEW,
                variables: { campaignId },
                fetchPolicy: 'network-only',
            })
            setOverview(data.licenseOverview)
            setFormOpen(true)
            return true
        } catch {
            return false
        } finally {
            setLoading(false)
        }
    }

    const saved = (savedOverview: LicenseOverview) => {
        setFormOpen(false)
        if (campaignId) setOverview(savedOverview)
        else draft?.onOverviewSaved(savedOverview)
    }

    const targetOf = (target: LicenseOverview | null = current) =>
        campaignId ? { campaignId } : { license: { ...draft?.fields, overview: overviewDraftInputOf(target) } }

    return {
        overview: current,
        formOpen,
        setFormOpen,
        loading,
        open,
        saved,
        targetOf,
        available: Boolean(campaignId || draft),
    }
}
